/**
 * QuoteOfTheDayBanner — home page rotating quote banner.
 *
 * - Shows 2 rotating quotes (from /api/quotes/daily).
 * - 30-second auto-rotate (paused on hover).
 * - Manual prev/next arrows.
 * - "Submit a Quote" CTA on the right side.
 * - Each quote author name deep-links to their author page.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ChevronLeft, ChevronRight, Quote as QuoteIcon, Send, ExternalLink } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const ROTATE_MS = 30_000;

export default function QuoteOfTheDayBanner() {
  const [quotes, setQuotes] = useState([]);
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/api/quotes/daily`)
      .then(r => setQuotes(r.data.quotes || []))
      .catch(() => {});
  }, []);

  const advance = useCallback((dir = 1) => {
    setIdx(i => {
      if (!quotes.length) return 0;
      return (i + dir + quotes.length) % quotes.length;
    });
  }, [quotes.length]);

  useEffect(() => {
    if (paused || quotes.length < 2) return;
    timerRef.current = setInterval(() => advance(1), ROTATE_MS);
    return () => clearInterval(timerRef.current);
  }, [paused, advance, quotes.length]);

  if (!quotes.length) return null;
  const q = quotes[idx];

  return (
    <section
      className="rounded-2xl overflow-hidden border relative"
      style={{ borderColor: "var(--wofy-border)", background: "linear-gradient(135deg, rgba(178,93,56,0.05), rgba(92,107,82,0.05))" }}
      data-testid="quote-of-the-day"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="grid md:grid-cols-[1fr_auto] gap-4 items-center px-6 md:px-10 py-6 md:py-8">
        {/* Left: the quote */}
        <div key={q.id} className="min-w-0 wofy-reveal">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-7 h-7 rounded-lg grid place-items-center text-white" style={{ background: "var(--wofy-primary)" }}>
              <QuoteIcon className="w-4 h-4" />
            </div>
            <div className="overline text-xs opacity-70">
              QUOTE OF THE DAY · {q.category_label || q.category}
            </div>
          </div>
          <blockquote className="font-display text-xl md:text-2xl leading-snug" data-testid="qotd-text">
            <Link to={`/quotes/${q.slug}`} className="hover:underline decoration-wavy underline-offset-4">
              “{q.text}”
            </Link>
          </blockquote>
          <div className="mt-3 flex items-center gap-3 flex-wrap text-sm opacity-90">
            <Link
              to={`/quotes/authors/${q.author_slug}`}
              className="font-medium hover:underline"
              data-testid="qotd-author"
            >
              — {q.author}
            </Link>
            {q.author_wikipedia && (
              <a
                href={q.author_wikipedia}
                target="_blank"
                rel="noreferrer"
                className="text-xs opacity-70 hover:opacity-100 inline-flex items-center gap-0.5"
                data-testid="qotd-wiki"
              >
                Wikipedia <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        </div>

        {/* Right: controls + CTA */}
        <div className="flex flex-col gap-3 items-stretch md:items-end">
          {quotes.length > 1 && (
            <div className="flex items-center gap-1" data-testid="qotd-controls">
              <button
                onClick={() => advance(-1)}
                aria-label="Previous quote"
                className="w-9 h-9 rounded-full border grid place-items-center hover:bg-white"
                style={{ borderColor: "var(--wofy-border)" }}
                data-testid="qotd-prev"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <div className="flex gap-1 mx-1">
                {quotes.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setIdx(i)}
                    aria-label={`Show quote ${i + 1}`}
                    className="w-1.5 h-1.5 rounded-full transition"
                    style={{ background: i === idx ? "var(--wofy-primary)" : "rgba(0,0,0,0.15)" }}
                  />
                ))}
              </div>
              <button
                onClick={() => advance(1)}
                aria-label="Next quote"
                className="w-9 h-9 rounded-full border grid place-items-center hover:bg-white"
                style={{ borderColor: "var(--wofy-border)" }}
                data-testid="qotd-next"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
          <Link
            to="/quotes/submit"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm hover:shadow-md transition text-white whitespace-nowrap"
            style={{ background: "var(--wofy-primary)" }}
            data-testid="qotd-submit"
          >
            <Send className="w-3.5 h-3.5" />Submit a Quote
          </Link>
        </div>
      </div>
    </section>
  );
}
